/**
 * Block inspector — edits the selected block's content, layout and, for the
 * form, its fields and post-submit actions.
 *
 * Text fields write on every keystroke because the preview is live: a "apply"
 * button between typing and seeing is the difference between composing a page
 * and filling a form about a page.
 */

import {
  CAMPOS_CANONICOS,
  ROTULOS_BLOCO,
  TIPOS_CAMPO,
  normalizarFormulario,
  novoCampo,
  type AcoesFormulario,
  type Bloco,
  type CampoFormulario,
  type LayoutBloco,
  type TipoCampo,
} from '@egba-do/shared'

import { AreaTexto, Aviso, Botao, Campo, Selecao } from '../ui'
import { EscolhaImagem } from './EscolhaImagem'

const FUNDOS = [
  { valor: 'claro', texto: 'Branco' },
  { valor: 'suave', texto: 'Cinza claro' },
  { valor: 'marca', texto: 'Cor da marca' },
  { valor: 'escuro', texto: 'Escuro' },
]

const ESPACAMENTOS = [
  { valor: 'compacto', texto: 'Compacto' },
  { valor: 'normal', texto: 'Normal' },
  { valor: 'amplo', texto: 'Amplo' },
]

const NOMES_TIPO_CAMPO: Record<TipoCampo, string> = {
  texto: 'Texto',
  email: 'E-mail',
  telefone: 'Telefone',
  cnpj: 'CNPJ',
  numero: 'Número',
  data: 'Data',
  selecao: 'Lista de opções',
  textarea: 'Texto longo',
  checkbox: 'Caixa de marcação',
}

/** Blocks whose `itens` array the operator edits directly. */
const COM_ITENS: Record<string, { rotulo: string; campos: Array<[string, string]> }> = {
  colunas: { rotulo: 'Colunas', campos: [['titulo', 'Título'], ['texto', 'Texto']] },
  beneficios: { rotulo: 'Itens', campos: [['titulo', 'Título'], ['texto', 'Texto']] },
  prova: { rotulo: 'Números', campos: [['valor', 'Valor'], ['rotulo', 'Rótulo']] },
  faq: { rotulo: 'Perguntas', campos: [['pergunta', 'Pergunta'], ['resposta', 'Resposta']] },
}

/** Text props each block type exposes, in the order they appear on the page. */
const TEXTOS: Record<string, Array<[string, string, 'linha' | 'area']>> = {
  hero: [
    ['selo', 'Selo', 'linha'],
    ['titulo', 'Título', 'linha'],
    ['subtitulo', 'Subtítulo', 'area'],
    ['textoBotao', 'Texto do botão', 'linha'],
  ],
  texto: [
    ['titulo', 'Título', 'linha'],
    ['corpo', 'Texto', 'area'],
  ],
  colunas: [['titulo', 'Título', 'linha']],
  beneficios: [['titulo', 'Título', 'linha']],
  prazo: [
    ['titulo', 'Título', 'linha'],
    ['texto', 'Texto', 'area'],
    ['referencia', 'Referência legal', 'linha'],
  ],
  prova: [['titulo', 'Título', 'linha']],
  faq: [['titulo', 'Título', 'linha']],
  formulario: [
    ['titulo', 'Título', 'linha'],
    ['subtitulo', 'Subtítulo', 'area'],
  ],
  cta: [
    ['titulo', 'Título', 'linha'],
    ['textoBotao', 'Texto do botão', 'linha'],
  ],
  contato: [
    ['titulo', 'Título', 'linha'],
    ['subtitulo', 'Subtítulo', 'linha'],
    ['whatsapp', 'WhatsApp (com DDD)', 'linha'],
    ['mensagemWhatsapp', 'Mensagem pré-preenchida do WhatsApp', 'area'],
    ['email', 'E-mail', 'linha'],
    ['assuntoEmail', 'Assunto pré-preenchido do e-mail', 'linha'],
  ],
  rodape: [
    ['texto', 'Texto', 'area'],
    ['contato', 'Contato', 'linha'],
  ],
}

export interface InspetorBlocoProps {
  bloco: Bloco | null
  campanhas: Array<{ id: string; nome: string }>
  categorias: Array<{ id: string; nome: string }>
  segmentos: Array<{ id: string; nome: string }>
  problemas: string[]
  onAlterar: (bloco: Bloco) => void
  onRemover: () => void
  onDuplicar?: () => void
  onGerarCopy?: (texto: string, aplicar: (novo: string) => void) => void
  gerandoCopy?: boolean
}

/** Blocks that carry a photograph. */
const COM_IMAGEM = new Set(['hero', 'imagem'])

export function InspetorBloco({
  bloco,
  campanhas,
  categorias,
  segmentos,
  problemas,
  onAlterar,
  onRemover,
  onDuplicar,
  onGerarCopy,
  gerandoCopy,
}: InspetorBlocoProps) {
  if (!bloco) {
    return (
      <div className="space-y-4 p-6">
        <p className="text-sm font-medium text-slate-700">Nada selecionado</p>
        <p className="text-sm text-slate-500">
          Clique num bloco da página, ao centro, para editar o conteúdo, a imagem e a aparência
          dele. Passando o mouse, cada bloco mostra o próprio nome.
        </p>
        <p className="text-sm text-slate-500">
          Para acrescentar, use os botões de bloco na coluna da esquerda. Para reordenar, arraste
          na lista <strong>Estrutura da página</strong>.
        </p>
      </div>
    )
  }

  const p = bloco.props as Record<string, unknown>
  const layout: LayoutBloco = bloco.layout ?? {}

  const setProp = (chave: string, valor: unknown) =>
    onAlterar({ ...bloco, props: { ...p, [chave]: valor } })
  const setLayout = (chave: keyof LayoutBloco, valor: unknown) =>
    onAlterar({ ...bloco, layout: { ...layout, [chave]: valor } })

  const itens = Array.isArray(p.itens) ? (p.itens as Array<Record<string, string>>) : []
  const setItens = (novos: Array<Record<string, string>>) => setProp('itens', novos)

  // Normalizado: abrir uma página do formato antigo tem de mostrar os campos
  // que ela de fato usa, não uma lista vazia.
  const { campos, acoes } = normalizarFormulario(p)
  const setAcoes = (chave: keyof AcoesFormulario, valor: unknown) =>
    setProp('acoes', { ...acoes, [chave]: valor })

  const config = COM_ITENS[bloco.tipo]

  return (
    <div className="space-y-6 p-5">
      <div>
        <p className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase">
          {ROTULOS_BLOCO[bloco.tipo]?.nome ?? bloco.tipo}
        </p>
        <p className="text-xs text-slate-500">{ROTULOS_BLOCO[bloco.tipo]?.descricao}</p>
      </div>

      {problemas.length > 0 && (
        <Aviso tom="perigo">
          <ul className="list-inside list-disc">
            {problemas.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </Aviso>
      )}

      {/* ------------------------------------------------------------- textos */}
      <div className="space-y-4">
        {(TEXTOS[bloco.tipo] ?? []).map(([chave, rotulo, forma]) =>
          forma === 'area' ? (
            <div key={chave}>
              <AreaTexto
                rotulo={rotulo}
                rows={4}
                value={String(p[chave] ?? '')}
                onChange={(e) => setProp(chave, e.target.value)}
              />
              {onGerarCopy && String(p[chave] ?? '').trim().length > 20 && (
                <button
                  type="button"
                  disabled={gerandoCopy}
                  onClick={() => onGerarCopy(String(p[chave]), (novo) => setProp(chave, novo))}
                  className="mt-1.5 text-xs font-medium text-marca-700 underline disabled:opacity-50"
                >
                  {gerandoCopy ? 'Reescrevendo…' : 'Reescrever com IA'}
                </button>
              )}
            </div>
          ) : (
            <Campo
              key={chave}
              rotulo={rotulo}
              value={String(p[chave] ?? '')}
              onChange={(e) => setProp(chave, e.target.value)}
            />
          ),
        )}
      </div>

      {/* -------------------------------------------------------------- itens */}
      {config && (
        <div className="space-y-3 border-t border-[color:var(--color-borda-suave)] pt-5">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-700">{config.rotulo}</p>
            <Botao
              variante="secundario"
              tamanho="sm"
              onClick={() =>
                setItens([...itens, Object.fromEntries(config.campos.map(([k]) => [k, '']))])
              }
            >
              + Adicionar
            </Botao>
          </div>

          {itens.map((item, i) => (
            <div
              key={i}
              className="space-y-3 rounded-lg border border-[color:var(--color-borda)] p-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">#{i + 1}</span>
                <div className="flex gap-1">
                  <BotaoIcone
                    titulo="Subir"
                    desabilitado={i === 0}
                    onClick={() => setItens(trocar(itens, i, i - 1))}
                  >
                    ↑
                  </BotaoIcone>
                  <BotaoIcone
                    titulo="Descer"
                    desabilitado={i === itens.length - 1}
                    onClick={() => setItens(trocar(itens, i, i + 1))}
                  >
                    ↓
                  </BotaoIcone>
                  <BotaoIcone
                    titulo="Remover"
                    onClick={() => setItens(itens.filter((_, j) => j !== i))}
                  >
                    ×
                  </BotaoIcone>
                </div>
              </div>
              {config.campos.map(([chave, rotulo]) => (
                <Campo
                  key={chave}
                  rotulo={rotulo}
                  value={item[chave] ?? ''}
                  onChange={(e) =>
                    setItens(itens.map((x, j) => (j === i ? { ...x, [chave]: e.target.value } : x)))
                  }
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ----------------------------------------------------------- contato */}
      {bloco.tipo === 'contato' && (
        <div className="space-y-2 border-t border-[color:var(--color-borda-suave)] pt-5">
          <AreaTexto
            rotulo="Telefones"
            rows={3}
            value={(Array.isArray(p.telefones) ? (p.telefones as string[]) : []).join('\n')}
            onChange={(e) =>
              setProp(
                'telefones',
                e.target.value.split('\n').map((t) => t.trimStart()),
              )
            }
            ajuda="Um número por linha. Cada um vira um cartão com link de ligação."
          />
          <p className="text-xs leading-relaxed text-slate-400">
            Canal sem valor não aparece na página — deixe o WhatsApp em branco enquanto a EGBA não
            tiver o número oficial.
          </p>
        </div>
      )}

      {/* --------------------------------------------------------- formulário */}
      {bloco.tipo === 'formulario' && (
        <>
          <div className="space-y-3 border-t border-[color:var(--color-borda-suave)] pt-5">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-700">Campos</p>
              <Botao
                variante="secundario"
                tamanho="sm"
                onClick={() => setProp('campos', [...campos, novoCampo()])}
              >
                + Campo
              </Botao>
            </div>

            {campos.map((c, i) => {
              const canonico = CAMPOS_CANONICOS[c.nome]
              return (
                <div
                  key={c.id}
                  className="space-y-3 rounded-lg border border-[color:var(--color-borda)] p-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-slate-400">{c.nome}</span>
                    <div className="flex gap-1">
                      <BotaoIcone
                        titulo="Subir"
                        desabilitado={i === 0}
                        onClick={() => setProp('campos', trocar(campos, i, i - 1))}
                      >
                        ↑
                      </BotaoIcone>
                      <BotaoIcone
                        titulo="Descer"
                        desabilitado={i === campos.length - 1}
                        onClick={() => setProp('campos', trocar(campos, i, i + 1))}
                      >
                        ↓
                      </BotaoIcone>
                      <BotaoIcone
                        titulo="Remover"
                        onClick={() =>
                          setProp(
                            'campos',
                            campos.filter((x) => x.id !== c.id),
                          )
                        }
                      >
                        ×
                      </BotaoIcone>
                    </div>
                  </div>

                  <Campo
                    rotulo="Rótulo"
                    value={c.rotulo}
                    onChange={(e) =>
                      setProp(
                        'campos',
                        campos.map((x) => (x.id === c.id ? { ...x, rotulo: e.target.value } : x)),
                      )
                    }
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <Selecao
                      rotulo="Tipo"
                      value={c.tipo}
                      disabled={Boolean(canonico)}
                      onChange={(e) =>
                        setProp(
                          'campos',
                          campos.map((x) =>
                            x.id === c.id ? { ...x, tipo: e.target.value as TipoCampo } : x,
                          ),
                        )
                      }
                      opcoes={TIPOS_CAMPO.map((t) => ({ valor: t, texto: NOMES_TIPO_CAMPO[t] }))}
                      ajuda={canonico ? 'Campo do cadastro — tipo fixo.' : undefined}
                    />
                    <Selecao
                      rotulo="Largura"
                      value={c.largura ?? 'inteira'}
                      onChange={(e) =>
                        setProp(
                          'campos',
                          campos.map((x) =>
                            x.id === c.id
                              ? { ...x, largura: e.target.value as CampoFormulario['largura'] }
                              : x,
                          ),
                        )
                      }
                      opcoes={[
                        { valor: 'inteira', texto: 'Linha inteira' },
                        { valor: 'metade', texto: 'Meia linha' },
                      ]}
                    />
                  </div>

                  {c.tipo === 'selecao' && (
                    <AreaTexto
                      rotulo="Opções"
                      rows={3}
                      value={(c.opcoes ?? []).join('\n')}
                      onChange={(e) =>
                        setProp(
                          'campos',
                          campos.map((x) =>
                            x.id === c.id ? { ...x, opcoes: e.target.value.split('\n') } : x,
                          ),
                        )
                      }
                      ajuda="Uma opção por linha."
                    />
                  )}

                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={Boolean(c.obrigatorio)}
                      onChange={(e) =>
                        setProp(
                          'campos',
                          campos.map((x) =>
                            x.id === c.id ? { ...x, obrigatorio: e.target.checked } : x,
                          ),
                        )
                      }
                      className="size-4"
                    />
                    Obrigatório
                  </label>
                </div>
              )
            })}
          </div>

          <div className="space-y-4 border-t border-[color:var(--color-borda-suave)] pt-5">
            <p className="text-sm font-medium text-slate-700">Depois do envio</p>

            <Selecao
              rotulo="O que acontece"
              value={acoes.destino ?? 'mensagem'}
              onChange={(e) => setAcoes('destino', e.target.value)}
              opcoes={[
                { valor: 'mensagem', texto: 'Mostrar mensagem de confirmação' },
                { valor: 'redirecionar', texto: 'Redirecionar para outra página' },
              ]}
            />

            {acoes.destino === 'redirecionar' ? (
              <Campo
                rotulo="URL de destino"
                placeholder="https://…"
                value={acoes.urlRedirecionamento ?? ''}
                onChange={(e) => setAcoes('urlRedirecionamento', e.target.value)}
              />
            ) : (
              <AreaTexto
                rotulo="Mensagem de confirmação"
                rows={3}
                value={acoes.mensagemSucesso ?? ''}
                onChange={(e) => setAcoes('mensagemSucesso', e.target.value)}
              />
            )}

            <Campo
              rotulo="Texto do botão"
              value={acoes.textoBotao ?? ''}
              onChange={(e) => setAcoes('textoBotao', e.target.value)}
            />

            <AreaTexto
              rotulo="Texto de consentimento"
              rows={3}
              value={acoes.textoConsentimento ?? ''}
              onChange={(e) => setAcoes('textoConsentimento', e.target.value)}
              ajuda="Obrigatório (LGPD, art. 8º). Não existe opção para remover a caixa."
            />

            <Selecao
              rotulo="Segmento da empresa"
              value={acoes.segmento ?? ''}
              onChange={(e) => setAcoes('segmento', e.target.value || undefined)}
              opcoes={[
                { valor: '', texto: 'Não classificar' },
                ...segmentos.map((s) => ({ valor: s.id, texto: s.nome })),
              ]}
              ajuda="Diz qual obrigação legal a empresa que preencher tem."
            />

            <Selecao
              rotulo="Categoria de publicação"
              value={acoes.categoriaId ?? ''}
              onChange={(e) => setAcoes('categoriaId', e.target.value || undefined)}
              opcoes={[
                { valor: '', texto: 'A da própria página' },
                ...categorias.map((c) => ({ valor: c.id, texto: c.nome })),
              ]}
            />

            <Selecao
              rotulo="Entrar na campanha"
              value={acoes.campanhaId ?? ''}
              onChange={(e) => setAcoes('campanhaId', e.target.value || undefined)}
              opcoes={[
                { valor: '', texto: 'Nenhuma' },
                ...campanhas.map((c) => ({ valor: c.id, texto: c.nome })),
              ]}
              ajuda="Quem preencher entra na régua dessa campanha automaticamente."
            />
          </div>
        </>
      )}

      {/* ------------------------------------------------------------ imagem */}
      {COM_IMAGEM.has(bloco.tipo) && (
        <div className="space-y-4 border-t border-[color:var(--color-borda-suave)] pt-5">
          <EscolhaImagem
            imagem={(p.imagem as never) ?? null}
            sugestao={
              bloco.tipo === 'hero'
                ? String(p.titulo ?? '')
                : String(p.legenda ?? p.titulo ?? '')
            }
            onEscolher={(img) => setProp('imagem', img)}
            onRemover={() => setProp('imagem', undefined)}
          />
          {bloco.tipo === 'imagem' && (
            <Campo
              rotulo="Legenda"
              value={String(p.legenda ?? '')}
              onChange={(e) => setProp('legenda', e.target.value)}
            />
          )}
        </div>
      )}

      {/* ------------------------------------------------------------ layout */}
      <div className="space-y-4 border-t border-[color:var(--color-borda-suave)] pt-5">
        <p className="text-sm font-medium text-slate-700">Aparência</p>

        <Selecao
          rotulo="Fundo"
          value={layout.fundo ?? 'claro'}
          onChange={(e) => setLayout('fundo', e.target.value)}
          opcoes={FUNDOS}
        />

        {config && (
          <Selecao
            rotulo="Colunas"
            value={String(layout.colunas ?? 3)}
            onChange={(e) => setLayout('colunas', Number(e.target.value))}
            opcoes={[
              { valor: '1', texto: '1 coluna' },
              { valor: '2', texto: '2 colunas' },
              { valor: '3', texto: '3 colunas' },
            ]}
            ajuda="No celular sempre vira uma coluna só."
          />
        )}

        <Selecao
          rotulo="Espaçamento"
          value={layout.espacamento ?? 'normal'}
          onChange={(e) => setLayout('espacamento', e.target.value)}
          opcoes={ESPACAMENTOS}
        />

        <Selecao
          rotulo="Alinhamento"
          value={layout.alinhamento ?? 'esquerda'}
          onChange={(e) => setLayout('alinhamento', e.target.value)}
          opcoes={[
            { valor: 'esquerda', texto: 'À esquerda' },
            { valor: 'centro', texto: 'Centralizado' },
          ]}
        />
      </div>

      <div className="flex gap-2 border-t border-[color:var(--color-borda-suave)] pt-5">
        {onDuplicar && (
          <Botao variante="secundario" onClick={onDuplicar}>
            Duplicar
          </Botao>
        )}
        <Botao variante="perigo" onClick={onRemover}>
          Remover bloco
        </Botao>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------

function trocar<T>(lista: T[], a: number, b: number): T[] {
  const copia = [...lista]
  const item = copia[a]!
  copia[a] = copia[b]!
  copia[b] = item
  return copia
}

function BotaoIcone({
  children,
  titulo,
  desabilitado,
  onClick,
}: {
  children: React.ReactNode
  titulo: string
  desabilitado?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      title={titulo}
      aria-label={titulo}
      disabled={desabilitado}
      onClick={onClick}
      className="flex size-6 items-center justify-center rounded text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
    >
      {children}
    </button>
  )
}
